import { useEffect, useMemo, useState } from "react";
import { CrmLead } from "@/lib/crm";
import {
  AFTER_SALE_STAGES, COMMISSION_ROLES, CONTRACT_STATUS, CONTRACT_TYPES, DEFAULT_AFTER_SALE_CHECKLIST,
  ErpAfterSale, ErpCommission, ErpContract, ErpTransaction, PAYMENT_STATUS, TRANSACTION_CATEGORIES,
  brl, createContractWithFinance, dateBR, deleteRow, fetchAfterSales, fetchCommissions, fetchContracts,
  fetchTransactions, financeSummary, insertRow, label, updateRow,
} from "@/lib/erp";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label as FieldLabel } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Building2, FileText, HeartHandshake, Percent, Plus, Trash2, Wallet } from "lucide-react";

type Tab = "contratos" | "comissoes" | "financeiro" | "pos_venda";

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
  { key: "contratos", label: "Contratos", icon: <FileText className="h-4 w-4" /> },
  { key: "comissoes", label: "Comissões", icon: <Percent className="h-4 w-4" /> },
  { key: "financeiro", label: "Financeiro", icon: <Wallet className="h-4 w-4" /> },
  { key: "pos_venda", label: "Pós-venda", icon: <HeartHandshake className="h-4 w-4" /> },
];

const today = () => new Date().toISOString().slice(0, 10);

interface Props {
  leads: CrmLead[];
}

const CrmErp = ({ leads }: Props) => {
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>("contratos");
  const [contracts, setContracts] = useState<ErpContract[]>([]);
  const [commissions, setCommissions] = useState<ErpCommission[]>([]);
  const [transactions, setTransactions] = useState<ErpTransaction[]>([]);
  const [afterSales, setAfterSales] = useState<ErpAfterSale[]>([]);

  const load = async () => {
    const [c, k, t, a] = await Promise.all([
      fetchContracts(), fetchCommissions(), fetchTransactions(), fetchAfterSales(),
    ]);
    setContracts(c);
    setCommissions(k);
    setTransactions(t);
    setAfterSales(a);
  };
  useEffect(() => {
    void load();
  }, []);

  /* ---------------- Contract form ---------------- */
  const [cForm, setCForm] = useState({
    lead_id: "", client_name: "", property_title: "", contract_type: "venda",
    value: "", commission_pct: "6", status: "em_negociacao", signed_at: "", broker: "", notes: "",
  });

  const saveContract = async () => {
    if (!cForm.client_name.trim() && !cForm.lead_id) {
      return toast({ title: "Informe o cliente", variant: "destructive" });
    }
    const lead = leads.find((l) => l.id === cForm.lead_id);
    const { error } = await createContractWithFinance(
      {
        lead_id: cForm.lead_id || null,
        client_name: cForm.client_name.trim() || lead?.client_name || "Cliente",
        property_title: cForm.property_title.trim() || lead?.property_name || null,
        contract_type: cForm.contract_type,
        value: Number(cForm.value || 0),
        commission_pct: Number(cForm.commission_pct || 0),
        status: cForm.status,
        signed_at: cForm.signed_at || null,
        notes: cForm.notes.trim() || null,
        contract_number: `TC-${Date.now().toString().slice(-6)}`,
      },
      cForm.broker.trim() || lead?.broker || undefined,
    );
    if (error) return toast({ title: "Erro", description: (error as any).message, variant: "destructive" });
    setCForm({ ...cForm, client_name: "", property_title: "", value: "", notes: "", lead_id: "" });
    toast({ title: "Contrato criado com comissão e recebível" });
    await load();
  };

  /* ---------------- Commission form ---------------- */
  const [kForm, setKForm] = useState({ contract_id: "", beneficiary: "", role: "vendedor", percentage: "", amount: "", due_date: "" });
  const saveCommission = async () => {
    if (!kForm.beneficiary.trim()) return toast({ title: "Informe o beneficiário", variant: "destructive" });
    const contract = contracts.find((c) => c.id === kForm.contract_id);
    const pct = Number(kForm.percentage || 0);
    const amount = Number(kForm.amount || 0) || (contract ? (Number(contract.value) * pct) / 100 : 0);
    const { error } = await insertRow("erp_commissions", {
      contract_id: kForm.contract_id || null,
      beneficiary: kForm.beneficiary.trim(),
      role: kForm.role,
      percentage: pct,
      amount,
      due_date: kForm.due_date || null,
    });
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    setKForm({ ...kForm, beneficiary: "", percentage: "", amount: "" });
    await load();
  };

  /* ---------------- Transaction form ---------------- */
  const [tForm, setTForm] = useState({
    kind: "receita", category: "Comissão", description: "", amount: "", due_date: today(), contract_id: "",
  });
  const saveTransaction = async () => {
    if (!tForm.description.trim()) return toast({ title: "Informe a descrição", variant: "destructive" });
    const { error } = await insertRow("erp_transactions", {
      kind: tForm.kind,
      category: tForm.category,
      description: tForm.description.trim(),
      amount: Number(tForm.amount || 0),
      due_date: tForm.due_date || null,
      contract_id: tForm.contract_id || null,
    });
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    setTForm({ ...tForm, description: "", amount: "" });
    await load();
  };

  /* ---------------- After sale form ---------------- */
  const [aForm, setAForm] = useState({ contract_id: "", client_name: "", stage: "entrega_chaves", next_action_at: "" });
  const saveAfterSale = async () => {
    const contract = contracts.find((c) => c.id === aForm.contract_id);
    const name = aForm.client_name.trim() || contract?.client_name;
    if (!name) return toast({ title: "Informe o cliente", variant: "destructive" });
    const { error } = await insertRow("erp_after_sales", {
      contract_id: aForm.contract_id || null,
      lead_id: contract?.lead_id || null,
      client_name: name,
      stage: aForm.stage,
      next_action_at: aForm.next_action_at || null,
      checklist: DEFAULT_AFTER_SALE_CHECKLIST.map((l) => ({ label: l, done: false })),
    });
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    setAForm({ ...aForm, client_name: "", contract_id: "" });
    await load();
  };

  const toggleChecklist = async (row: ErpAfterSale, index: number) => {
    const checklist = (row.checklist || []).map((c, i) => (i === index ? { ...c, done: !c.done } : c));
    await updateRow("erp_after_sales", row.id, { checklist });
    await load();
  };

  const summary = useMemo(() => financeSummary(transactions), [transactions]);
  const contractTotals = useMemo(() => {
    const active = contracts.filter((c) => !["cancelado"].includes(c.status));
    return {
      vgv: active.reduce((a, c) => a + Number(c.value || 0), 0),
      comissao: active.reduce((a, c) => a + Number(c.commission_value || 0), 0),
      fechados: contracts.filter((c) => ["assinado", "escritura", "concluido"].includes(c.status)).length,
    };
  }, [contracts]);

  const contractName = (id: string | null) =>
    contracts.find((c) => c.id === id)?.client_name || "—";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        {[
          ["VGV em contratos", brl(contractTotals.vgv)],
          ["Comissão prevista", brl(contractTotals.comissao)],
          ["Contratos fechados", String(contractTotals.fechados)],
          ["Recebido", brl(summary.receitaPaga)],
          ["A receber", brl(summary.receitaPrevista)],
          ["Saldo projetado", brl(summary.saldoProjetado)],
        ].map(([k, v]) => (
          <Card key={k} className="p-3 border border-border">
            <p className="font-body text-[11px] text-muted-foreground">{k}</p>
            <p className="font-display text-base font-bold text-foreground">{v}</p>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 rounded-md text-sm font-body border flex items-center gap-2 transition-colors ${
              tab === t.key
                ? "bg-accent text-accent-foreground border-accent font-semibold"
                : "bg-card text-muted-foreground border-border hover:border-accent/50"
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* ------------- CONTRATOS ------------- */}
      {tab === "contratos" && (
        <div className="space-y-4">
          <Card className="p-4 border border-border space-y-3">
            <h3 className="font-display text-sm font-bold text-foreground flex items-center gap-2">
              <Plus className="h-4 w-4" /> Novo contrato
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <FieldLabel className="font-body text-xs">Lead do CRM</FieldLabel>
                <Select value={cForm.lead_id} onValueChange={(v) => setCForm({ ...cForm, lead_id: v })}>
                  <SelectTrigger className="font-body h-9"><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent>
                    {leads.map((l) => (
                      <SelectItem key={l.id} value={l.id} className="font-body">{l.client_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel className="font-body text-xs">Cliente</FieldLabel>
                <Input className="font-body h-9" value={cForm.client_name} onChange={(e) => setCForm({ ...cForm, client_name: e.target.value })} />
              </div>
              <div>
                <FieldLabel className="font-body text-xs">Imóvel</FieldLabel>
                <Input className="font-body h-9" value={cForm.property_title} onChange={(e) => setCForm({ ...cForm, property_title: e.target.value })} />
              </div>
              <div>
                <FieldLabel className="font-body text-xs">Tipo</FieldLabel>
                <Select value={cForm.contract_type} onValueChange={(v) => setCForm({ ...cForm, contract_type: v })}>
                  <SelectTrigger className="font-body h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTRACT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value} className="font-body">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel className="font-body text-xs">Valor (R$)</FieldLabel>
                <Input type="number" className="font-body h-9" value={cForm.value} onChange={(e) => setCForm({ ...cForm, value: e.target.value })} />
              </div>
              <div>
                <FieldLabel className="font-body text-xs">Comissão (%)</FieldLabel>
                <Input type="number" step="0.1" className="font-body h-9" value={cForm.commission_pct} onChange={(e) => setCForm({ ...cForm, commission_pct: e.target.value })} />
              </div>
              <div>
                <FieldLabel className="font-body text-xs">Situação</FieldLabel>
                <Select value={cForm.status} onValueChange={(v) => setCForm({ ...cForm, status: v })}>
                  <SelectTrigger className="font-body h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTRACT_STATUS.map((t) => (
                      <SelectItem key={t.value} value={t.value} className="font-body">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel className="font-body text-xs">Assinatura</FieldLabel>
                <Input type="date" className="font-body h-9" value={cForm.signed_at} onChange={(e) => setCForm({ ...cForm, signed_at: e.target.value })} />
              </div>
              <div>
                <FieldLabel className="font-body text-xs">Corretor</FieldLabel>
                <Input className="font-body h-9" value={cForm.broker} onChange={(e) => setCForm({ ...cForm, broker: e.target.value })} />
              </div>
            </div>
            <Textarea rows={2} placeholder="Observações" className="font-body" value={cForm.notes} onChange={(e) => setCForm({ ...cForm, notes: e.target.value })} />
            <Button size="sm" variant="gold" onClick={saveContract}>Salvar contrato</Button>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {contracts.map((c) => (
              <Card key={c.id} className="p-4 border border-border">
                <div className="flex items-start gap-2">
                  <div className="min-w-0">
                    <p className="font-display text-sm font-bold text-foreground truncate">{c.client_name}</p>
                    <p className="font-body text-xs text-muted-foreground truncate">
                      <Building2 className="inline h-3 w-3" /> {c.property_title || "Imóvel não informado"} · {c.contract_number}
                    </p>
                  </div>
                  <Badge variant="outline" className="ml-auto font-body text-[10px]">{label(CONTRACT_STATUS, c.status)}</Badge>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="font-body text-[10px]">{label(CONTRACT_TYPES, c.contract_type)}</Badge>
                  <Badge variant="outline" className="font-body text-[10px]">{brl(c.value)}</Badge>
                  <Badge variant="outline" className="font-body text-[10px]">Comissão {brl(c.commission_value)} ({c.commission_pct}%)</Badge>
                  <Badge variant="outline" className="font-body text-[10px]">Assinado {dateBR(c.signed_at)}</Badge>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Select value={c.status} onValueChange={async (v) => { await updateRow("erp_contracts", c.id, { status: v, closed_at: v === "concluido" ? today() : c.closed_at }); await load(); }}>
                    <SelectTrigger className="font-body h-8 w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONTRACT_STATUS.map((t) => (
                        <SelectItem key={t.value} value={t.value} className="font-body">{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={async () => { await deleteRow("erp_contracts", c.id); await load(); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
            {contracts.length === 0 && <p className="font-body text-xs text-muted-foreground">Nenhum contrato cadastrado.</p>}
          </div>
        </div>
      )}

      {/* ------------- COMISSÕES ------------- */}
      {tab === "comissoes" && (
        <div className="space-y-4">
          <Card className="p-4 border border-border space-y-3">
            <h3 className="font-display text-sm font-bold text-foreground">Nova comissão</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select value={kForm.contract_id} onValueChange={(v) => setKForm({ ...kForm, contract_id: v })}>
                <SelectTrigger className="font-body h-9"><SelectValue placeholder="Contrato" /></SelectTrigger>
                <SelectContent>
                  {contracts.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="font-body">{c.client_name} — {brl(c.value)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input className="font-body h-9" placeholder="Beneficiário" value={kForm.beneficiary} onChange={(e) => setKForm({ ...kForm, beneficiary: e.target.value })} />
              <Select value={kForm.role} onValueChange={(v) => setKForm({ ...kForm, role: v })}>
                <SelectTrigger className="font-body h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMMISSION_ROLES.map((t) => (
                    <SelectItem key={t.value} value={t.value} className="font-body">{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" step="0.1" className="font-body h-9" placeholder="%" value={kForm.percentage} onChange={(e) => setKForm({ ...kForm, percentage: e.target.value })} />
              <Input type="number" className="font-body h-9" placeholder="Valor (R$) — opcional" value={kForm.amount} onChange={(e) => setKForm({ ...kForm, amount: e.target.value })} />
              <Input type="date" className="font-body h-9" value={kForm.due_date} onChange={(e) => setKForm({ ...kForm, due_date: e.target.value })} />
            </div>
            <Button size="sm" variant="gold" onClick={saveCommission}>Salvar comissão</Button>
          </Card>

          <div className="space-y-2">
            {commissions.map((k) => (
              <Card key={k.id} className="p-3 border border-border flex flex-wrap items-center gap-2">
                <div className="min-w-0">
                  <p className="font-display text-sm font-bold text-foreground truncate">{k.beneficiary}</p>
                  <p className="font-body text-xs text-muted-foreground">
                    {label(COMMISSION_ROLES, k.role)} · {contractName(k.contract_id)} · vence {dateBR(k.due_date)}
                  </p>
                </div>
                <Badge variant="outline" className="font-body text-[10px]">{k.percentage}%</Badge>
                <Badge variant="outline" className="font-body text-[10px]">{brl(k.amount)}</Badge>
                <div className="ml-auto flex items-center gap-2">
                  <Select value={k.status} onValueChange={async (v) => { await updateRow("erp_commissions", k.id, { status: v, paid_at: v === "pago" ? today() : null }); await load(); }}>
                    <SelectTrigger className="font-body h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_STATUS.map((t) => (
                        <SelectItem key={t.value} value={t.value} className="font-body">{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={async () => { await deleteRow("erp_commissions", k.id); await load(); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
            {commissions.length === 0 && <p className="font-body text-xs text-muted-foreground">Nenhuma comissão lançada.</p>}
          </div>
        </div>
      )}

      {/* ------------- FINANCEIRO ------------- */}
      {tab === "financeiro" && (
        <div className="space-y-4">
          <Card className="p-4 border border-border space-y-3">
            <h3 className="font-display text-sm font-bold text-foreground">Novo lançamento</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Select value={tForm.kind} onValueChange={(v) => setTForm({ ...tForm, kind: v })}>
                <SelectTrigger className="font-body h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="receita" className="font-body">Receita</SelectItem>
                  <SelectItem value="despesa" className="font-body">Despesa</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tForm.category} onValueChange={(v) => setTForm({ ...tForm, category: v })}>
                <SelectTrigger className="font-body h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRANSACTION_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="font-body">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input className="font-body h-9" placeholder="Descrição" value={tForm.description} onChange={(e) => setTForm({ ...tForm, description: e.target.value })} />
              <Input type="number" className="font-body h-9" placeholder="Valor (R$)" value={tForm.amount} onChange={(e) => setTForm({ ...tForm, amount: e.target.value })} />
              <Input type="date" className="font-body h-9" value={tForm.due_date} onChange={(e) => setTForm({ ...tForm, due_date: e.target.value })} />
              <Select value={tForm.contract_id} onValueChange={(v) => setTForm({ ...tForm, contract_id: v })}>
                <SelectTrigger className="font-body h-9"><SelectValue placeholder="Contrato (opcional)" /></SelectTrigger>
                <SelectContent>
                  {contracts.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="font-body">{c.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="gold" onClick={saveTransaction}>Lançar</Button>
          </Card>

          <div className="space-y-2">
            {transactions.map((t) => (
              <Card key={t.id} className="p-3 border border-border flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={`font-body text-[10px] ${t.kind === "receita" ? "text-emerald-600" : "text-destructive"}`}>
                  {t.kind === "receita" ? "Receita" : "Despesa"}
                </Badge>
                <div className="min-w-0">
                  <p className="font-display text-sm font-bold text-foreground truncate">{t.description}</p>
                  <p className="font-body text-xs text-muted-foreground">
                    {t.category} · vence {dateBR(t.due_date)} {t.contract_id ? `· ${contractName(t.contract_id)}` : ""}
                  </p>
                </div>
                <Badge variant="outline" className="font-body text-[10px]">{brl(t.amount)}</Badge>
                <div className="ml-auto flex items-center gap-2">
                  <Select value={t.status} onValueChange={async (v) => { await updateRow("erp_transactions", t.id, { status: v, paid_at: v === "pago" ? today() : null }); await load(); }}>
                    <SelectTrigger className="font-body h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_STATUS.map((s) => (
                        <SelectItem key={s.value} value={s.value} className="font-body">{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={async () => { await deleteRow("erp_transactions", t.id); await load(); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
            {transactions.length === 0 && <p className="font-body text-xs text-muted-foreground">Nenhum lançamento financeiro.</p>}
          </div>
        </div>
      )}

      {/* ------------- PÓS-VENDA ------------- */}
      {tab === "pos_venda" && (
        <div className="space-y-4">
          <Card className="p-4 border border-border space-y-3">
            <h3 className="font-display text-sm font-bold text-foreground">Novo pós-venda</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Select value={aForm.contract_id} onValueChange={(v) => setAForm({ ...aForm, contract_id: v })}>
                <SelectTrigger className="font-body h-9"><SelectValue placeholder="Contrato" /></SelectTrigger>
                <SelectContent>
                  {contracts.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="font-body">{c.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input className="font-body h-9" placeholder="Cliente" value={aForm.client_name} onChange={(e) => setAForm({ ...aForm, client_name: e.target.value })} />
              <Select value={aForm.stage} onValueChange={(v) => setAForm({ ...aForm, stage: v })}>
                <SelectTrigger className="font-body h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AFTER_SALE_STAGES.map((s) => (
                    <SelectItem key={s.value} value={s.value} className="font-body">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="date" className="font-body h-9" value={aForm.next_action_at} onChange={(e) => setAForm({ ...aForm, next_action_at: e.target.value })} />
            </div>
            <Button size="sm" variant="gold" onClick={saveAfterSale}>Criar acompanhamento</Button>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {afterSales.map((a) => (
              <Card key={a.id} className="p-4 border border-border">
                <div className="flex items-center gap-2">
                  <p className="font-display text-sm font-bold text-foreground truncate">{a.client_name}</p>
                  <Badge variant="outline" className="ml-auto font-body text-[10px]">{label(AFTER_SALE_STAGES, a.stage)}</Badge>
                </div>
                <p className="mt-1 font-body text-xs text-muted-foreground">
                  Próxima ação: {dateBR(a.next_action_at)}
                </p>
                <div className="mt-3 space-y-1.5">
                  {(a.checklist || []).map((item, i) => (
                    <label key={item.label} className="flex items-center gap-2 font-body text-xs text-muted-foreground cursor-pointer">
                      <Checkbox checked={item.done} onCheckedChange={() => toggleChecklist(a, i)} />
                      <span className={item.done ? "line-through" : ""}>{item.label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Select value={a.stage} onValueChange={async (v) => { await updateRow("erp_after_sales", a.id, { stage: v }); await load(); }}>
                    <SelectTrigger className="font-body h-8 w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AFTER_SALE_STAGES.map((s) => (
                        <SelectItem key={s.value} value={s.value} className="font-body">{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={async () => { await deleteRow("erp_after_sales", a.id); await load(); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
            {afterSales.length === 0 && <p className="font-body text-xs text-muted-foreground">Nenhum pós-venda em andamento.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmErp;
